import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import {
  clearAuthSession,
  extractIdTokenFromAuthPayload,
  extractRefreshTokenFromAuthPayload,
  getAuthToken,
  getStoredUser,
  saveAuthSession,
} from '../services/authSession';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAuthStatus = () => {
      try {
        const storedUser = getStoredUser();
        const idToken = getAuthToken();

        if (storedUser && idToken) {
          setUser(storedUser);
        } else {
          clearAuthSession();
        }
      } catch (_) {
        clearAuthSession();
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (credentials) => {
    try {
      setError(null);
      setLoading(true);

      const result = await authService.login(credentials);

      if (result.success) {
        const idToken = extractIdTokenFromAuthPayload(result.data);
        saveAuthSession({
          idToken,
          refreshToken: extractRefreshTokenFromAuthPayload(result.data),
          user: result.data?.user,
        });

        setUser(result.data.user);
        return { success: true };
      }
      setError(result.error || 'Login failed');
      return { success: false, error: result.error };
    } catch (error) {
      const errorMessage = error.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (userData) => {
    try {
      setError(null);
      setLoading(true);

      const result = await authService.signup(userData);

      if (result.success) {
        const idToken = extractIdTokenFromAuthPayload(result.data);
        saveAuthSession({
          idToken,
          refreshToken: extractRefreshTokenFromAuthPayload(result.data),
          user: result.data?.user,
        });

        setUser(result.data.user);
        return { success: true };
      }
      setError(result.error || 'Signup failed');
      return { success: false, error: result.error };
    } catch (error) {
      const errorMessage = error.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearAuthSession();
    setUser(null);
    setError(null);
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    signup,
    logout,
    clearError,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
